import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useNotify } from "../../../context/NotificationContext";
import {
  Card,
  Table,
  Input,
  Button,
  Typography,
  Avatar,
  Tag,
} from "antd";
import {
  PlusOutlined,
  SearchOutlined,
  EditOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { useCommon } from "../../../context/CommonContext";
import masterService from "../../../services/masterService";
import { getImageUrl } from "../../../utils/commonFunctions";

const { Title, Text } = Typography;
const { Search } = Input;

const GetAllCast = () => {
  const navigate = useNavigate();
  const { setPageTitle } = useCommon();
  const notify = useNotify();

  // State management
  const [loading, setLoading] = useState(false);
  const [talents, setTalents] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
    showSizeChanger: true,
    showQuickJumper: true,
    showTotal: (total, range) =>
      `${range[0]}-${range[1]} of ${total} talents`,
  });

  // Filters
  const [filters, setFilters] = useState({
    search: "",
  });

  // Typing fired one request per keystroke. The term is now searched once the
  // typing settles, on the same 300ms the users and promos lists use.
  const searchDebounceRef = useRef(null);
  const SEARCH_DEBOUNCE_MS = 300;
  // Monotonic request id — responses from superseded requests are dropped so a
  // slow earlier search can't overwrite the results of the latest one.
  const requestSeqRef = useRef(0);

  const getTalents = useCallback(
    async (params = {}) => {
      const requestId = ++requestSeqRef.current;
      try {
        setLoading(true);

        const requestParams = {
          perPage: pagination.pageSize,
          currentPage: pagination.current,
          searchText: filters.search,
          ...params,
        };

        console.log("Fetching talents with params:", requestParams);
        const response = await masterService.getTalentList(requestParams);
        if (requestId !== requestSeqRef.current) return; // superseded by a newer request
        console.log("Talents API Response:", response);

        if (response.data && response.data.status) {
          const talentsData = response.data.data?.data || [];
          const paginationData = response.data.data?.pagination || {};

          console.log("Talents data:", talentsData);
          console.log("Pagination data:", paginationData);

          // Ensure talentsData is an array
          if (Array.isArray(talentsData)) {
            setTalents(talentsData);
            setPagination((prev) => ({
              ...prev,
              total: paginationData.total || talentsData.length,
              current: paginationData.currentPage || prev.current,
            }));
          } else {
            console.error("Talents data is not an array:", talentsData);
            setTalents([]);
          }
        } else {
          notify.error({ message: "Failed to fetch talents" });
          setTalents([]);
        }
      } catch (error) {
        if (requestId !== requestSeqRef.current) return;
        console.error("Error fetching talents:", error);
        notify.error({ message: "Failed to fetch talents" });
        setTalents([]);
      } finally {
        // Only the newest request clears the spinner — an overtaken one finishing
        // early would otherwise show the list as loaded while it is still loading.
        if (requestId === requestSeqRef.current) setLoading(false);
      }
    },
    [filters.search, pagination.pageSize, pagination.current, notify]
  );

  // Load talents data. Mount only: every later fetch is an explicit getTalents(...)
  // from the search or pagination handler. This effect used to re-run whenever
  // getTalents was rebuilt, which — because the search term is one of its
  // dependencies — meant a request on every keystroke.
  useEffect(() => {
    setPageTitle("Talents");
    getTalents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearchChange = (e) => {
    const value = e.target.value ?? "";
    setFilters((prev) => ({ ...prev, search: value }));
    setPagination((prev) => ({ ...prev, current: 1 }));
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      getTalents({ searchText: value.trim(), currentPage: 1 });
      searchDebounceRef.current = null;
    }, SEARCH_DEBOUNCE_MS);
  };

  // Enter key / search icon applies immediately, cancelling any pending debounce
  // so the term is not searched twice.
  const handleSearchNow = () => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    getTalents({ searchText: (filters.search || "").trim(), currentPage: 1 });
  };

  useEffect(() => {
    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
  }, []);

  // Handle table change (pagination, sorting, filtering)
  const handleTableChange = (paginationInfo, tableFilters, sorter) => {
    const { current, pageSize } = paginationInfo;

    setPagination((prev) => ({
      ...prev,
      current,
      pageSize,
    }));

    getTalents({
      currentPage: current,
      perPage: pageSize,
      searchText: filters.search,
    });
  };

  // Handle add talent
  const handleAddTalent = () => {
    navigate("/talent/edit/new");
  };

  // Handle edit talent
  const handleEditTalent = (talentId) => {
    navigate(`/talent/edit/${talentId}`);
  };

  // Table columns
  const columns = [
    {
      title: "Image",
      dataIndex: "image_url",
      key: "image_url",
      width: 100,
      render: (imageUrl) => {
        if (imageUrl) {
          return (
            <Avatar
              src={getImageUrl(imageUrl)}
              size={64}
              icon={<UserOutlined />}
              shape="square"
            />
          );
        }
        return <Avatar size={64} icon={<UserOutlined />} shape="square" />;
      },
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      defaultSortOrder: "ascend",
      sorter: (a, b) => (a.name || "").localeCompare(b.name || ""),
      render: (text) => (
        <Text strong style={{ fontSize: "14px" }}>
          {text}
        </Text>
      ),
    },
    {
      title: "Alternate Name",
      dataIndex: "alternateName",
      key: "alternateName",
      render: (text) => (
        <Text style={{ fontSize: "14px", color: "rgba(0, 0, 0, 0.65)" }}>
          {text || "-"}
        </Text>
      ),
    },
    {
      title: "Job Title",
      dataIndex: "job_title",
      key: "job_title",
      render: (job_title) => {
        if (!job_title) return <Text type="secondary">-</Text>;
        const titles = Array.isArray(job_title) ? job_title : job_title.split(",");
        return (
          <div>
            {titles.map((title, index) => (
              <Tag key={index} color="blue" style={{ marginBottom: 4 }}>
                {title}
              </Tag>
            ))}
          </div>
        );
      },
    },
    {
      title: "Gender",
      dataIndex: "gender",
      key: "gender",
      width: 100,
      render: (gender) => {
        if (!gender) return <Text type="secondary">-</Text>;
        const colorMap = {
          Male: "blue",
          Female: "pink",
          Other: "default",
        };
        return <Tag color={colorMap[gender] || "default"}>{gender}</Tag>;
      },
    },
    {
      title: "Nationality",
      dataIndex: "nationality",
      key: "nationality",
      render: (text) => (
        <Text style={{ fontSize: "14px" }}>{text || "-"}</Text>
      ),
    },
    {
      title: "IMDB ID",
      dataIndex: "imdb_id",
      key: "imdb_id",
      width: 120,
      render: (text) => (
        <Text style={{ fontSize: "14px" }}>{text || "-"}</Text>
      ),
    },
    {
      title: "Actions",
      key: "action",
      width: 100,
      fixed: "right",
      render: (text, record) => (
        <Button
          type="link"
          icon={<EditOutlined />}
          onClick={() => handleEditTalent(record.talent_id)}
          size="small"
        >
          Edit
        </Button>
      ),
    },
  ];

  return (
    <Card>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: "16px",
          gap: "16px",
          flexWrap: "wrap",
        }}
      >
        <Title level={4} style={{ margin: 0 }}>
          Talents ({pagination.total})
        </Title>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "12px",
            minWidth: 300,
            flex: 1,
            justifyContent: "flex-end",
          }}
        >
          <Search
            placeholder="Search talents..."
            allowClear
            value={filters.search}
            onChange={handleSearchChange}
            onSearch={handleSearchNow}
            style={{ width: 320, maxWidth: "40vw" }}
            enterButton={<SearchOutlined />}
          />
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleAddTalent}
            size="middle"
          >
            Add Talent
          </Button>
        </div>
      </div>

      <Table
        columns={columns}
        dataSource={talents}
        rowKey="talent_id"
        loading={loading}
        pagination={pagination}
        onChange={handleTableChange}
        scroll={{ x: 1200 }}
        size="middle"
        locale={{
          emptyText: "No talents found",
          filterConfirm: "OK",
          filterReset: "Reset",
          filterEmptyText: "No filters",
        }}
      />
    </Card>
  );
};

export default GetAllCast;
